import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader, PrimaryButton, StatusBadge } from '@/components'
import { HomeDashboardService, type HomeDashboardData, type HomeInspectionSummary } from '@/db'
import { paths } from '@/routes/paths'
import { formatCivilDate, formatDateTime } from '@/utils/formatters'

type DashboardState =
  { status: 'loading' } | { status: 'ready'; data: HomeDashboardData } | { status: 'error' }

interface HomePageProps {
  loadDashboard?: () => Promise<HomeDashboardData>
}

const defaultDashboardLoader = () => new HomeDashboardService().load(4)

const baseActions = [
  {
    to: paths.newReport,
    title: 'Novo relatório',
    description: 'Iniciar uma inspeção técnica',
    symbol: '+',
  },
  {
    to: paths.reports,
    title: 'Continuar relatório',
    description: 'Retomar um rascunho salvo',
    symbol: '↻',
  },
  { to: paths.clients, title: 'Clientes', description: 'Consultar a base local', symbol: 'C' },
  { to: paths.machines, title: 'Máquinas', description: 'Localizar equipamentos', symbol: 'M' },
  {
    to: paths.technicians,
    title: 'Técnicos',
    description: 'Cadastrar e gerenciar a equipe',
    symbol: 'T',
  },
  { to: paths.reports, title: 'Histórico', description: 'Ver inspeções anteriores', symbol: 'H' },
  { to: paths.backup, title: 'Backup', description: 'Proteger os dados do tablet', symbol: 'B' },
  {
    to: paths.settings,
    title: 'Configurações',
    description: 'Ajustar preferências e contatos',
    symbol: '⚙',
  },
]

const statusLabels = {
  draft: 'Rascunho',
  completed: 'Concluída',
  cancelled: 'Cancelada',
} as const

function greeting(name?: string) {
  if (!name) return 'Olá'
  return `Olá, ${name.split(/\s+/)[0]}`
}

function RecentInspectionItem({ inspection }: { inspection: HomeInspectionSummary }) {
  const critical = inspection.priority === 'urgent_stop_immediately'
  const tone = critical
    ? 'critical'
    : inspection.status === 'completed'
      ? 'good'
      : inspection.status === 'draft'
        ? 'attention'
        : 'neutral'

  return (
    <li>
      <Link
        className="inspection-row"
        to={
          inspection.status === 'draft'
            ? paths.inspection(inspection.id)
            : paths.report(inspection.id)
        }
      >
        <span className="inspection-row__main">
          <span className="inspection-row__topline">
            <strong>{inspection.reportNumber}</strong>
            <StatusBadge tone={tone}>
              {critical ? 'Crítica' : statusLabels[inspection.status]}
            </StatusBadge>
          </span>
          <span>{inspection.companyName}</span>
          <small>{inspection.machineName}</small>
        </span>
        <time dateTime={inspection.inspectionDate}>
          {formatCivilDate(inspection.inspectionDate)}
        </time>
      </Link>
    </li>
  )
}

function DashboardSkeleton() {
  return (
    <div className="dashboard-skeleton" role="status" aria-label="Carregando painel">
      <span className="sr-only">Carregando informações do painel…</span>
      {Array.from({ length: 6 }, (_, index) => (
        <span className="skeleton-block" key={index} />
      ))}
    </div>
  )
}

export function HomePage({ loadDashboard = defaultDashboardLoader }: HomePageProps = {}) {
  const [state, setState] = useState<DashboardState>({ status: 'loading' })
  const [requestNumber, setRequestNumber] = useState(0)

  useEffect(() => {
    let active = true
    void loadDashboard()
      .then((data) => {
        if (active) setState({ status: 'ready', data })
      })
      .catch(() => {
        if (active) setState({ status: 'error' })
      })
    return () => {
      active = false
    }
  }, [loadDashboard, requestNumber])

  const dashboard = state.status === 'ready' ? state.data : undefined
  const counts = dashboard?.counts
  const actions = baseActions.map((action) =>
    action.title === 'Continuar relatório' && counts
      ? {
          ...action,
          description:
            counts.draftInspections === 1
              ? '1 rascunho disponível'
              : `${counts.draftInspections} rascunhos disponíveis`,
        }
      : action,
  )

  const summary = [
    { label: 'Relatórios concluídos', value: counts?.completedInspections ?? 0, tone: 'good' },
    { label: 'Em andamento', value: counts?.draftInspections ?? 0, tone: 'attention' },
    { label: 'Clientes ativos', value: counts?.activeClients ?? 0, tone: 'neutral' },
    { label: 'Máquinas ativas', value: counts?.activeMachines ?? 0, tone: 'neutral' },
    { label: 'Inspeções críticas', value: counts?.criticalInspections ?? 0, tone: 'critical' },
    {
      label: 'Último backup',
      value: dashboard?.lastBackupAt ? formatDateTime(dashboard.lastBackupAt) : 'Nunca',
      tone: dashboard?.backupPending ? 'attention' : 'good',
    },
  ] as const

  return (
    <div className="page-stack home-page">
      <PageHeader
        eyebrow="Operação de campo"
        title={greeting(dashboard?.defaultTechnicianName)}
        description="Seu painel de inspeções, pronto para trabalhar no tablet."
        action={<StatusBadge tone="good">Dados locais</StatusBadge>}
      />

      <section aria-labelledby="home-actions-title">
        <div className="section-heading section-heading--compact">
          <h2 id="home-actions-title">Acesso rápido</h2>
        </div>
        <div className="action-grid">
          {actions.map((action) => (
            <Link className="action-card" to={action.to} key={`${action.to}-${action.title}`}>
              <span className="action-card__symbol" aria-hidden="true">
                {action.symbol}
              </span>
              <span className="action-card__copy">
                <strong>{action.title}</strong>
                <span>{action.description}</span>
              </span>
              <span className="action-card__arrow" aria-hidden="true">
                →
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section aria-labelledby="home-summary-title">
        <div className="section-heading section-heading--compact">
          <h2 id="home-summary-title">Resumo</h2>
          <p>Informações calculadas diretamente no banco local.</p>
        </div>
        {state.status === 'loading' ? (
          <DashboardSkeleton />
        ) : state.status === 'error' ? (
          <div className="inline-error" role="alert">
            <div>
              <strong>Não foi possível carregar o painel.</strong>
              <p>Seus dados continuam no tablet. Tente novamente.</p>
            </div>
            <PrimaryButton onClick={() => setRequestNumber((current) => current + 1)}>
              Tentar novamente
            </PrimaryButton>
          </div>
        ) : (
          <div className="summary-grid" data-testid="summary-grid">
            {summary.map((item) => (
              <article className={`summary-card summary-card--${item.tone}`} key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </article>
            ))}
          </div>
        )}
      </section>

      {state.status === 'ready' ? (
        <div className="dashboard-columns">
          <section className="dashboard-panel" aria-labelledby="recent-drafts-title">
            <div className="panel-heading">
              <div>
                <h2 id="recent-drafts-title">Rascunhos recentes</h2>
                <p>Até quatro relatórios em andamento.</p>
              </div>
              <Link to={paths.reports}>Ver todos</Link>
            </div>
            {state.data.recentDrafts.length ? (
              <ul className="inspection-list">
                {state.data.recentDrafts.map((inspection) => (
                  <RecentInspectionItem inspection={inspection} key={inspection.id} />
                ))}
              </ul>
            ) : (
              <div className="compact-empty">
                <strong>Nenhum rascunho</strong>
                <span>Os relatórios iniciados aparecerão aqui.</span>
              </div>
            )}
          </section>

          <section className="dashboard-panel" aria-labelledby="recent-inspections-title">
            <div className="panel-heading">
              <div>
                <h2 id="recent-inspections-title">Inspeções recentes</h2>
                <p>Últimas conclusões e cancelamentos.</p>
              </div>
              <Link to={paths.reports}>Histórico</Link>
            </div>
            {state.data.recentInspections.length ? (
              <ul className="inspection-list">
                {state.data.recentInspections.map((inspection) => (
                  <RecentInspectionItem inspection={inspection} key={inspection.id} />
                ))}
              </ul>
            ) : (
              <div className="compact-empty">
                <strong>Nenhuma inspeção concluída</strong>
                <span>O histórico recente aparecerá aqui.</span>
              </div>
            )}
          </section>
        </div>
      ) : null}
    </div>
  )
}
